import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Zap, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FAQ from "../components/FAQ";
import CheckoutSocialProof from "../components/CheckoutSocialProof";
import PlanUpsell from "../components/PlanUpsell";
import { api } from "../lib/api";

const STYLES = ["Cartoony / Playful", "Professional / Corporate", "Minimal / Clean", "Bold / Edgy", "Luxury / Premium", "Friendly / Casual"];

const OFFER_MAP = {
  "pack-99": {
    pkg: "one_time_99",
    title: "1 Week Done For You Content Pack",
    price: 99.99,
    period: "one time",
    headline: "Get a week of content. For $99.99. Done in 72–96 hours.",
    sub: "5 professional short-form videos + 5 branded social images, tailored to your business — without a single phone call.",
    bullets: [
      "5 short-form videos (TikTok / Reels / Shorts)",
      "5 branded images (square + story formats)",
      "Custom to your brand voice, niche, and audience",
      "Delivered in 72–96 hours via Google Drive (depending on project size)",
      "Zero contracts. Zero subscriptions.",
    ],
    valueStack: [
      ["5 short-form videos", "$500"],
      ["5 branded images", "$250"],
      ["Brand-tailored design", "$150"],
      ["72–96h rush delivery", "$100"],
      ["Done-for-you process", "$50"],
    ],
    totalValue: 1050,
    cta: "Pay $99.99 — Start Now",
  },
  "monthly-175": {
    pkg: "monthly_175",
    title: "Done For You Monthly Content Plan",
    price: 175,
    period: "per month",
    headline: "10 videos + 10 images. Every month. $175. Cancel anytime.",
    sub: "Never run out of content again. We deliver fresh, on-brand content every 30 days so you can stop worrying about posting and focus on running your business.",
    bullets: [
      "10 short-form videos every month",
      "10 branded images every month",
      "Priority 72–96h delivery",
      "Continuously tailored to your brand",
      "Cancel anytime. No fees. No retention calls.",
    ],
    valueStack: [
      ["10 short-form videos / mo", "$1,000"],
      ["10 branded images / mo", "$500"],
      ["Priority delivery", "$200"],
      ["Ongoing brand alignment", "$300"],
      ["Cancel anytime", "Priceless"],
    ],
    totalValue: 2000,
    cta: "Start Monthly Plan — $175/mo",
  },
};

export default function OfferPage({ slug }) {
  const offer = OFFER_MAP[slug];
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [contentStyle, setContentStyle] = useState("Professional / Corporate");
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadSaving, setLeadSaving] = useState(false);
  // Active plan the customer will be charged for. Starts as the page's plan,
  // but the upsell card lets pack-99 visitors switch to monthly inline.
  const [selectedPkg, setSelectedPkg] = useState(offer?.pkg);

  // Look up the offer config by package_id so the CTA/price/checkout reflect the active choice.
  const activeOffer =
    Object.values(OFFER_MAP).find((o) => o.pkg === selectedPkg) || offer;

  // Track offer view on mount
  useEffect(() => {
    if (!offer) return;
    api.post("/track", {
      event: "view_offer",
      path: window.location.pathname,
      package_id: offer.pkg,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const validLead = email.includes("@") && businessName.trim() && industry.trim();

  const saveLead = async () => {
    if (!validLead) return;
    setLeadSaving(true);
    try {
      await api.post("/lead", {
        email,
        business_name: businessName.trim(),
        industry: industry.trim(),
        content_style: contentStyle,
        package_id: selectedPkg,
      });
      setLeadCaptured(true);
      // Track lead form completion
      api.post("/track", { event: "lead_form_complete", package_id: selectedPkg }).catch(() => {});
    } catch (e) {
      console.error(e);
      // Still allow checkout even if lead save failed (don't block paying customers)
      setLeadCaptured(true);
    } finally {
      setLeadSaving(false);
    }
  };

  const checkout = async () => {
    if (!acceptedTerms) {
      alert("Please accept the Terms of Service to continue.");
      return;
    }
    if (!leadCaptured) {
      await saveLead();
    }
    try {
      setLoading(true);
      const res = await api.post("/checkout/session", {
        package_id: selectedPkg,
        origin_url: window.location.origin,
        customer_email: email,
      });
      // Track pay-click event
      api.post("/track", { event: "pay_click", package_id: selectedPkg }).catch(() => {});
      // Facebook Pixel: InitiateCheckout (deduplicated with backend CAPI via shared event_id)
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq(
          "track",
          "InitiateCheckout",
          {
            content_ids: [selectedPkg],
            value: activeOffer.price,
            currency: "USD",
          },
          { eventID: res.data.event_id_initiate }
        );
      }
      window.location.href = res.data.url;
    } catch (e) {
      const serverMsg = e?.response?.data?.detail;
      if (serverMsg) {
        alert(serverMsg);
      } else {
        alert("We couldn't start checkout. Please try again or email contentsnapbrand@gmail.com to complete your order manually.");
      }
      setLoading(false);
    }
  };

  if (!offer) {
    return (
      <div className="min-h-screen bg-[#080C14] text-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-4xl font-bold">Offer not found</h1>
          <Link to="/" className="btn-primary mt-6 inline-flex" data-testid="back-home-btn">Back to home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-grain min-h-screen" data-testid={`offer-page-${slug}`}>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-16 md:pt-20 pb-20">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 relative">
            <div className="corner-bracket tl" />
            <div className="corner-bracket tr" />
            <div className="pl-2 md:pl-6 pr-2 pt-6 pb-2">
              <span className="pill" data-testid="offer-hero-pill">
                <Zap size={12} /> {slug === "pack-99" ? "ONE-TIME PACK" : "MONTHLY PLAN"}
              </span>
              <h1 className="rise rise-2 mt-5 font-display font-black text-white text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.05]">
                {offer.headline}
              </h1>
              <p className="rise rise-3 mt-5 text-lg text-[#A0AABF] max-w-2xl">{offer.sub}</p>

              <div className="rise rise-4 mt-8 max-w-md space-y-3">
                <input
                  type="email"
                  required
                  placeholder="Your email (for delivery + confirmation)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="snap-input"
                  data-testid="offer-email-input"
                />
                <input
                  type="text"
                  required
                  placeholder="Your business name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="snap-input"
                  data-testid="offer-business-input"
                />
                <input
                  type="text"
                  required
                  placeholder="Your industry (e.g. roofing, salon, fitness coach)"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="snap-input"
                  data-testid="offer-industry-input"
                />
                <select
                  className="snap-input"
                  value={contentStyle}
                  onChange={(e) => setContentStyle(e.target.value)}
                  data-testid="offer-style-input"
                >
                  {STYLES.map((s) => (
                    <option key={s} value={s} className="bg-[#0B111D]">{s}</option>
                  ))}
                </select>
                <label className="flex items-start gap-3 text-sm text-[#A0AABF] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#3B9EFF]"
                    data-testid="offer-accept-terms"
                  />
                  <span>
                    I have read and accept the{" "}
                    <Link to="/terms" target="_blank" className="text-[#3B9EFF] hover:underline">Terms of Service</Link>
                    {" "}— <span className="text-white font-semibold">all sales final, one revision per order, no refunds.</span>
                  </span>
                </label>
              </div>

              {slug === "pack-99" && (
                <PlanUpsell
                  selectedPkg={selectedPkg}
                  onSelect={(pkg) => {
                    setSelectedPkg(pkg);
                    api
                      .post("/track", {
                        event: pkg === "monthly_175" ? "upsell_chose_monthly" : "upsell_chose_one_time",
                        package_id: pkg,
                      })
                      .catch(() => {});
                  }}
                />
              )}

              <CheckoutSocialProof price={activeOffer.price} />

              <div className="rise rise-4 mt-6 flex flex-wrap gap-4 items-center">
                <button
                  onClick={checkout}
                  disabled={loading || leadSaving || !acceptedTerms || !validLead}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="offer-checkout-btn"
                >
                  {loading ? "Loading..." : leadSaving ? "Saving..." : activeOffer.cta} <ArrowRight size={18} />
                </button>
                <div className="flex items-center gap-3 text-sm text-[#A0AABF]">
                  <ShieldCheck size={16} className="text-[#3B9EFF]" /> Secure Square checkout
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2 items-center text-xs text-[#A0AABF]">
                <span className="font-accent">PAY WITH</span>
                <span className="px-2 py-1 rounded bg-white/5 border border-[#3B9EFF]/15">Card</span>
                <span className="px-2 py-1 rounded bg-white/5 border border-[#3B9EFF]/15">Apple Pay</span>
                <span className="px-2 py-1 rounded bg-white/5 border border-[#3B9EFF]/15">Google Pay</span>
                <span className="px-2 py-1 rounded bg-white/5 border border-[#3B9EFF]/15">Cash App Pay</span>
              </div>
            </div>
          </div>
        </section>

        {/* Pain → Solution */}
        <section className="py-20 border-t border-[#3B9EFF]/10">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-10">
            <div>
              <span className="pill">The Reality</span>
              <h2 className="mt-5 font-display font-bold text-white text-3xl md:text-4xl tracking-tighter">
                You're a business owner — not a content creator.
              </h2>
              <p className="mt-4 text-[#A0AABF] leading-relaxed">
                Every "I'll post tomorrow" turns into a week of silence. Every week of silence is a customer scrolling past your dead feed and into your competitor's.
              </p>
              <p className="mt-4 text-[#A0AABF] leading-relaxed">
                Hiring a social media manager? $500–$1,500/month and they still ghost you. Doing it yourself? You don't have time and you know it.
              </p>
            </div>
            <div className="snap-card">
              <span className="font-accent text-xs text-[#3B9EFF]">THE FIX</span>
              <h3 className="mt-3 font-display text-2xl font-bold text-white">
                We make it. You post it. You grow.
              </h3>
              <ul className="mt-5 space-y-3">
                {offer.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/90">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#3B9EFF]/15 border border-[#3B9EFF]/40 flex items-center justify-center text-[#3B9EFF] shrink-0">
                      <Check size={12} />
                    </span>
                    <span className="text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Value Stack */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <div className="text-center">
              <span className="pill">Value Stack</span>
              <h2 className="mt-5 font-display font-bold text-white text-4xl tracking-tighter">
                Worth <span className="line-through text-[#A0AABF]">${offer.totalValue.toLocaleString()}</span>. You pay <span className="text-[#3B9EFF]">${offer.price}</span>.
              </h2>
            </div>
            <div className="snap-card mt-10 relative">
              <div className="corner-bracket tl" />
              <div className="corner-bracket tr" />
              <div className="corner-bracket bl" />
              <div className="corner-bracket br" />
              <ul className="divide-y divide-[#3B9EFF]/10">
                {offer.valueStack.map(([label, retail], i) => (
                  <li key={i} className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-[#3B9EFF]/15 border border-[#3B9EFF]/40 flex items-center justify-center text-[#3B9EFF]">
                        <Check size={14} />
                      </span>
                      <span className="text-white font-medium">{label}</span>
                    </div>
                    <span className="font-display font-bold text-[#A0AABF] line-through">{retail}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-[#3B9EFF]/20 flex items-center justify-between">
                <div>
                  <p className="text-[#A0AABF] text-xs font-accent">YOU PAY</p>
                  <p className="font-display text-5xl font-black text-[#3B9EFF]">${offer.price}<span className="text-base text-[#A0AABF] font-normal">/{offer.period}</span></p>
                </div>
                <button
                  onClick={checkout}
                  disabled={loading}
                  className="btn-primary"
                  data-testid="offer-checkout-btn-2"
                >
                  {loading ? "Loading..." : activeOffer.cta} <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Reversal */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-5">
            {[
              { icon: Clock, t: "72–96h Delivery", d: "Depending on project size. One revision included." },
              { icon: ShieldCheck, t: "No Refunds", d: slug === "pack-99" ? "All sales final. One revision per order." : "Cancel anytime. No prorated refunds." },
              { icon: Zap, t: "Zero Hassle", d: "No calls, no Zooms, no chasing." },
            ].map((b, i) => (
              <div key={i} className="snap-card text-center">
                <div className="mx-auto w-11 h-11 rounded-md bg-[#3B9EFF]/10 border border-[#3B9EFF]/30 flex items-center justify-center text-[#3B9EFF]">
                  <b.icon size={20} />
                </div>
                <h4 className="mt-4 font-display text-lg font-bold text-white">{b.t}</h4>
                <p className="mt-1 text-[#A0AABF] text-sm">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        <FAQ />

        {/* Final CTA */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center relative">
            <div className="corner-bracket tl" />
            <div className="corner-bracket tr" />
            <div className="corner-bracket bl" />
            <div className="corner-bracket br" />
            <div className="py-12">
              <h2 className="font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                One decision. <span className="text-[#3B9EFF]">Massive return.</span>
              </h2>
              <p className="mt-4 text-[#A0AABF]">Pay now. Fill a 2-minute form. Get content in 72–96 hours.</p>
              <button
                onClick={checkout}
                disabled={loading}
                className="btn-primary mt-6"
                data-testid="offer-checkout-btn-3"
              >
                {loading ? "Loading..." : activeOffer.cta} <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
